import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme/theme';
import { AuthProvider, ProtectedRoute, useAuth } from './context/AuthContext';
import { PageHeaderProvider } from './context/PageHeaderContext';
import { ConnectionStatusProvider } from './context/ConnectionStatusContext';
import signalRService from './services/signalr';
import PermissionGuard from './components/PermissionGuard';
import InstallPrompt from './components/InstallPrompt';
import PushNotificationPrompt from './components/PushNotificationPrompt';
import { Sections } from './hooks/usePermission';

// Lazy-load pages
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const AlarmsPage = React.lazy(() => import('./pages/AlarmsPage'));
const PlantDetail = React.lazy(() => import('./pages/PlantDetail'));
const PlantSettings = React.lazy(() => import('./pages/PlantSettings'));
const Layout = React.lazy(() => import('./components/Layout'));
const AdminSettings = React.lazy(() => import('./pages/AdminSettings'));
const DocumentsPage = React.lazy(() => import('./pages/DocumentsPage'));
const TrendsPage = React.lazy(() => import('./pages/TrendsPage'));
const StatisticsPage = React.lazy(() => import('./pages/StatisticsPage'));
const SynopticPage = React.lazy(() => import('./pages/SynopticPage'));
const QrLoginPage = React.lazy(() => import('./pages/QrLoginPage'));

// Gateway page (tabs: Stato, Diagnostica, Lifecycle, Console)
const GatewayOverviewPage = React.lazy(() => import('./pages/GatewayOverviewPage'));

// Configurazioni pages
const VariablesPage = React.lazy(() => import('./pages/VariablesPage'));
const PlantGatewayTab = React.lazy(() => import('./pages/PlantGatewayTab'));
const SynopticSettingsPage = React.lazy(() => import('./pages/SynopticSettingsPage'));
const StatisticsSettingsTab = React.lazy(() => import('./pages/StatisticsSettingsTab'));
const AlertSettingsTab = React.lazy(() => import('./pages/AlertSettingsTab'));
const VoiceAssistantPage = React.lazy(() => import('./pages/VoiceAssistantPage'));
const DatabaseMaintenancePage = React.lazy(() => import('./pages/DatabaseMaintenancePage'));
const MaintenancePage = React.lazy(() => import('./pages/MaintenancePage'));
const MaintComponentDetailPage = React.lazy(() => import('./pages/MaintComponentDetailPage'));
const MaintInterventionDetailPage = React.lazy(() => import('./pages/MaintInterventionDetailPage'));
const MaintScanPage = React.lazy(() => import('./pages/MaintScanPage'));
const PlantCamerasConfigPage = React.lazy(() => import('./pages/PlantCamerasConfigPage'));
const CameraMosaicPage = React.lazy(() => import('./pages/CameraMosaicPage'));
const RemoteAccessSettingsPage = React.lazy(() => import('./pages/RemoteAccessSettingsPage'));
const RemoteAccessMfaPage = React.lazy(() => import('./pages/RemoteAccessMfaPage'));

const SignalRConnector: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      signalRService.startConnection().catch((err) => {
        console.error('[App] Errore connessione SignalR:', err);
      });
    } else {
      signalRService.stopConnection();
    }
    return () => {
      signalRService.stopConnection();
    };
  }, [isAuthenticated]);

  return <>{children}</>;
};

const DocumentsPageWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <DocumentsPage plantId={id || ''} />;
};

// Route wrappers for pages that need plantId as prop
const PlantIdWrapper: React.FC<{ Component: React.FC<{ plantId: string; plantName?: string }> }> = ({ Component }) => {
  const { id } = useParams<{ id: string }>();
  return <Component plantId={id || ''} />;
};

const VariablesWrapper: React.FC = () => <PlantIdWrapper Component={VariablesPage} />;
const DriversWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <PlantGatewayTab plantId={id || ''} mode="drivers" />;
};
const StatisticsSettingsWrapper: React.FC = () => <PlantIdWrapper Component={StatisticsSettingsTab} />;
const AlertSettingsWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <AlertSettingsTab plantId={id || ''} plantName="" />;
};
const AlarmsWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <AlarmsPage plantId={id} />;
};

const AppRoutes: React.FC = () => (
  <SignalRConnector>
    <React.Suspense
      fallback={
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#aaa' }}>
          Caricamento...
        </div>
      }
    >
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/qr-login" element={<QrLoginPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="plant/:id" element={<PlantDetail />} />
          <Route path="plant/:id/alarms" element={<AlarmsWrapper />} />
          <Route path="plant/:id/settings" element={<PermissionGuard section={Sections.PlantSettings}><PlantSettings /></PermissionGuard>} />
          <Route path="plant/:id/documents" element={<PermissionGuard section={Sections.Documents}><DocumentsPageWrapper /></PermissionGuard>} />
          <Route path="plant/:id/trends" element={<PermissionGuard section={Sections.Trends}><TrendsPage /></PermissionGuard>} />
          <Route path="plant/:id/synoptic" element={<PermissionGuard section={Sections.SynopticView}><SynopticPage /></PermissionGuard>} />
          <Route path="plant/:id/synoptic/:pageId" element={<PermissionGuard section={Sections.SynopticView}><SynopticPage /></PermissionGuard>} />
          <Route path="plant/:id/statistics" element={<PermissionGuard section={Sections.Statistics}><StatisticsPage /></PermissionGuard>} />

          {/* Gateway (tabs: Stato, Diagnostica, Lifecycle, Console) */}
          <Route path="plant/:id/gateway" element={<PermissionGuard section={Sections.Gateway}><GatewayOverviewPage /></PermissionGuard>} />

          {/* Maintenance Module */}
          <Route path="plant/:id/maintenance" element={<PermissionGuard section={Sections.MaintenanceDashboard}><MaintenancePage /></PermissionGuard>} />
          <Route path="plant/:id/maintenance/component/:componentId" element={<PermissionGuard section={Sections.MaintenanceDashboard}><MaintComponentDetailPage /></PermissionGuard>} />
          <Route path="plant/:id/maintenance/intervention/:interventionId" element={<PermissionGuard section={Sections.MaintenanceDashboard}><MaintInterventionDetailPage /></PermissionGuard>} />
          <Route path="plant/:id/maintenance/scan" element={<PermissionGuard section={Sections.MaintenanceDashboard}><MaintScanPage /></PermissionGuard>} />

          {/* Database Maintenance */}
          <Route path="plant/:id/database" element={<PermissionGuard section={Sections.PlantSettings}><DatabaseMaintenancePage /></PermissionGuard>} />

          {/* Configurazioni section */}
          <Route path="plant/:id/config/variables" element={<PermissionGuard section={Sections.PlantSettings}><VariablesWrapper /></PermissionGuard>} />
          <Route path="plant/:id/config/drivers" element={<PermissionGuard section={Sections.Gateway}><DriversWrapper /></PermissionGuard>} />
          <Route path="plant/:id/config/synoptic" element={<PermissionGuard section={Sections.SynopticEdit}><SynopticSettingsPage /></PermissionGuard>} />
          <Route path="plant/:id/config/statistics" element={<PermissionGuard section={Sections.PlantSettings}><StatisticsSettingsWrapper /></PermissionGuard>} />
          <Route path="plant/:id/config/alerts" element={<PermissionGuard section={Sections.AlertRules}><AlertSettingsWrapper /></PermissionGuard>} />
          <Route path="plant/:id/config/voice-assistant" element={<PermissionGuard section={Sections.AlertRules}><VoiceAssistantPage /></PermissionGuard>} />
          {/* /plant/:id/cameras → mosaico per impianto (la pagina "Camere" tradizionale e' stata rimossa) */}
          <Route path="plant/:id/cameras" element={<PermissionGuard section={Sections.CamerasView}><PlantIdWrapper Component={CameraMosaicPage} /></PermissionGuard>} />
          <Route path="plant/:id/config/cameras" element={<PermissionGuard section={Sections.CamerasManage}><PlantCamerasConfigPage /></PermissionGuard>} />
          <Route path="plant/:id/config/remote-access" element={<PermissionGuard section={Sections.RemoteAccess}><RemoteAccessSettingsPage /></PermissionGuard>} />
          {/* Gateway Connect — landing per push MFA notification (aperta dal service worker) */}
          {/* Auth-required ma niente PermissionGuard: il check permission lo fa il backend su /sessions/approve */}
          <Route path="remote-access-mfa" element={<RemoteAccessMfaPage />} />

          {/* Mosaico camere cross-plant (tutte le camere accessibili all'utente) */}
          <Route path="cameras" element={<PermissionGuard section={Sections.CamerasView}><CameraMosaicPage /></PermissionGuard>} />

          <Route path="admin" element={<PermissionGuard section={Sections.AdminSystem}><AdminSettings /></PermissionGuard>} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  </SignalRConnector>
);

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <ConnectionStatusProvider>
            <PageHeaderProvider>
              <AppRoutes />
              <InstallPrompt />
              <PushNotificationPrompt />
            </PageHeaderProvider>
          </ConnectionStatusProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
